/**
 * Hook for fetching parent assets via graph traversal
 *
 * Queries for assets that discovered the current asset through DISCOVERED or
 * HAS_ATTRIBUTE relationships (2 hops away in the graph).
 */

import { useGraphQuery } from '@/hooks/useGraphQuery';
import { TreeNode } from '@/types';

export interface UseParentAssetsQueryResult {
  /** Parent assets tree structure from graph query */
  data: TreeNode[] | undefined;
  /** True if query is in initial loading state */
  isLoading: boolean;
  /** Query status */
  status: 'pending' | 'error' | 'success';
}

/**
 * Custom hook for fetching parent assets that discovered the current asset
 *
 * Traverses the graph to find assets connected through:
 * - DISCOVERED relationship (direct discovery)
 * - HAS_ATTRIBUTE relationship (attribute-based discovery)
 *
 * Uses 2-hop relationship traversal to find immediate parents.
 *
 * @param assetKey - The asset key to find parents for
 * @returns Parent assets tree data with loading state
 */
export function useParentAssetsQuery(
  assetKey: string
): UseParentAssetsQueryResult {
  const { data, isLoading, status } = useGraphQuery({
    tree: true,
    query: {
      node: {
        labels: ['Asset'],
        self_edges: false,
        relationships: [
          {
            label: ['DISCOVERED', 'HAS_ATTRIBUTE'],
            length: 2,
            target: {
              labels: ['Asset'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: assetKey,
                },
              ],
            },
          },
        ],
      },
    },
  });

  return {
    data,
    isLoading,
    status,
  };
}
